import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * EventAggregationApi
 * Admin endpoints for managing all analytics data aggregation pipelines
 */
export const EventAggregationApi = {
  // ── Generic Usage Events ───────────────────────────────────────────

  /**
   * Get generic event aggregation status
   * Returns last aggregated timestamp, pending records, and last run duration
   */
  getAggregationStatus: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregation-status`, {
      method: 'GET',
    });
  },

  /**
   * Execute generic event aggregation
   * Aggregates raw events into hourly metrics (event_metrics_hourly)
   *
   * @param {Object} params - Optional parameters
   * @param {string} params.startDate - ISO timestamp for start date (optional, omit for incremental)
   * @param {string} params.endDate - ISO timestamp for end date (optional, defaults to NOW)
   */
  executeAggregation: async (params = {}) => {
    const url = new URL(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregate`);
    
    if (params.startDate) {
      url.searchParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      url.searchParams.append('endDate', params.endDate);
    }
    
    return fetchWithAuth(url.toString(), {
      method: 'POST',
    });
  },

  // ── GPT Metrics ────────────────────────────────────────────────────

  /**
   * Get GPT metrics aggregation status
   */
  getGptAggregationStatus: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/gpt-aggregation-status`, {
      method: 'GET',
    });
  },

  /**
   * Execute GPT prompt event aggregation
   * Aggregates raw gpt_prompt_event into gpt_metrics_hourly
   */
  executeGptAggregation: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregate-gpt`, {
      method: 'POST',
    });
  },

  // ── Payment Metrics ────────────────────────────────────────────────

  /**
   * Get payment metrics aggregation status
   */
  getPaymentAggregationStatus: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/payment-aggregation-status`, {
      method: 'GET',
    });
  },

  /**
   * Execute payment event aggregation
   * Aggregates raw payment_events into payment_metrics_daily
   */
  executePaymentAggregation: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregate-payments`, {
      method: 'POST',
    });
  },

  // ── Learning Sessions ──────────────────────────────────────────────

  /**
   * Get learning session aggregation status
   */
  getLearningSessionAggregationStatus: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/learning-session-aggregation-status`, {
      method: 'GET',
    });
  },

  /**
   * Execute learning session aggregation
   * Aggregates generic usage events into learning_session_summary
   */
  executeLearningSessionAggregation: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregate-learning-sessions`, {
      method: 'POST',
    });
  },

  // ── User Profiles ──────────────────────────────────────────────────

  /**
   * Refresh user_learning_profile materialized view
   */
  refreshUserProfiles: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/refresh-user-profiles`, {
      method: 'POST',
    });
  },

  // ── Alert Evaluation ───────────────────────────────────────────────

  /**
   * Evaluate all active alert rules
   */
  evaluateAlerts: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/evaluate-alerts`, {
      method: 'POST',
    });
  },

  // ── Run All ────────────────────────────────────────────────────────

  /**
   * Execute all aggregation pipelines in sequence
   * Runs: generic events → GPT metrics → payment metrics → learning sessions → user profiles
   */
  executeAllAggregations: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/admin/analytics/aggregate-all`, {
      method: 'POST',
    });
  },
};
